import React, {
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Checkbox,
  DataCard,
  ProductCard,
  ProductDetail,
  ProductName,
} from 'anf-core-react';

import Clickable from '../clickable/clickable';
import ColorSizeText from '../color-size-text/color-size-text';
import ExchangeButton from '../exchange-button/exchange-button';
import ExchangeProductImage from '../exchange-product-image/exchange-product-image';
import ProductExchangeSurvey from '../product-exchange-survey/product-exchange-survey';
import TmntText from '../tmnt-text/tmnt-text';
import returnReasonsShape from '../return-reasons-shape';
import { productShape } from '../product-category-list/products-shape';
import ReturnReason from '../return-reason/return-reason';

function ProductExchangeCard({
  product,
  exchange,
  exchangeReasons,
  returnReasons,
  onChange,
  checked,
  selectedProductComments,
  updateComments,
  variant,
  showReturnReason,
}) {
  // Clicks on the Product Card when it is not selected will check the checkbox on
  // That card
  const handleProductCardClick = useCallback(
    () => onChange(product.orderLineKey, true),
    [onChange, product],
  );

  const canReturnProduct = useMemo(
    () => !product.finalSaleItem
      && (!product.returnDetails || product.returnable)
      && !!onChange
      && product.returnable,
    [product],
  );

  const hasGiftCard = useMemo(
    () => !!product?.gcFacePlateImageUrl,
    [product],
  );

  // Product Survey represents the drop down menu and Additional information text area
  // when the checkbox is selected next to a product.
  const productCardFooter = useMemo(
    () => (checked ? (
      <div className="product-exchange-card-product-card-footer">
        <ProductExchangeSurvey
          options={exchangeReasons}
          returnReasons={returnReasons}
          product={product}
          canExchange={product.exchangeable && !exchange}
          onChange={updateComments}
          selectedProductComments={selectedProductComments}
        />
        {exchange && (
          <DataCard
            variant="bordered"
            headerTitle="Exchanging for a new color or size"
            headerContent={(
              <ExchangeButton product={product} />
            )}
          >
            <div className="exchange-wrapper">
              <ExchangeProductImage imageId={exchange.imageId} collection={exchange.collection} />
              <div className="product-exchange-card-detail">
                <div className="product-exchange-card-detail-info">
                  <ColorSizeText product={exchange} />
                </div>
              </div>
            </div>
          </DataCard>
        )}
      </div>
    ) : null),
    [
      checked,
      exchangeReasons,
      selectedProductComments,
      product,
      updateComments,
      exchange,
      returnReasons,
    ],
  );

  // Items flagged as "Final Sale Items" will have a badge on the product image
  const finalSaleBadge = useMemo(() => (
    product.finalSaleItem || hasGiftCard ? <Badge isAttached><TmntText k="DRE_FINAL_SALE" /></Badge> : null
  ), [product, hasGiftCard]);

  // Items that have already been returned will have a badge on the product image
  const returnableBadge = useMemo(() => (
    product.returned ? <Badge isAttached><TmntText k="DRE_RETURNED" /></Badge> : null
  ), [product]);

  // Other items that cannot be returned
  const unreturnableBadge = useMemo(
    () => (
      (!hasGiftCard && !product.finalSaleItem && !product.returnDetails && !product.returnable)
        ? <Badge isAttached><TmntText k="DRE_NOT_SHIPPED" /></Badge> : null),
    [product, hasGiftCard],
  );

  // Display a checkbox next to products that are valid to return such as items
  // that are not final sale items and items that have not already been returned.
  const checkbox = useMemo(() => {
    if (!canReturnProduct) return <span />;

    return (
      <Checkbox
        id={`product-exchange-card-checkbox-${product.orderLineKey}`}
        value="on"
        isChecked={checked}
        onChange={(e) => onChange(product.orderLineKey, e.target.checked)}
        description=""
      />
    );
  }, [product, checked, onChange, canReturnProduct]);

  // Create the component to render the product name
  const productName = useMemo(() => (
    product.name ? <ProductName data-testid="product-exchange-product-name">{product.name}</ProductName> : <span />
  ), [product]);

  return (
    <div
      className={`product-exchange-card${checked ? ' active' : ''}`}
    >
      <Clickable
        isClickable={!checked && canReturnProduct}
        onClick={handleProductCardClick}
        testId={`product-exchange-card-${product.primeLineKey}`}
      >
        <ProductCard
          badge={finalSaleBadge || returnableBadge || unreturnableBadge || <span />}
          variant={variant}
          productPrice={<span />}
          header={<span />}
          productName={productName}
          productImage={(
            <ExchangeProductImage
              imageId={product.imageId}
              gcFacePlateImageUrl={product.gcFacePlateImageUrl}
            />
          )}
          footer={productCardFooter}
        >
          <ProductDetail>
            <div className="product-exchange-card-detail">
              <div className="product-exchange-card-detail-sku">
                <TmntText k="BAG_SKU" />
                {product.partNumber}
              </div>
              <div className="product-exchange-card-detail-info">
                <ColorSizeText product={product} />
              </div>
              {showReturnReason && (
                <ReturnReason
                  selectedProductComments={selectedProductComments[product.orderLineKey]}
                  orderLineKey={product.orderLineKey}
                />
              )}
            </div>
            {checkbox}
          </ProductDetail>
        </ProductCard>
      </Clickable>
    </div>
  );
}

export default ProductExchangeCard;

ProductExchangeCard.propTypes = {
  product: productShape,
  exchange: PropTypes.shape({
    swatch: PropTypes.string,
    sizePrimry: PropTypes.string,
    sizeSecondary: PropTypes.string,
    imageId: PropTypes.string,
    collection: PropTypes.string,
  }),
  selectedProductComments: PropTypes.shape({
    exchangeReason: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
    exchangeSecondaryReason: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
    }),
    exchangeComment: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  updateComments: PropTypes.func,
  checked: PropTypes.bool,
  exchangeReasons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
  ),
  returnReasons: returnReasonsShape,
  variant: PropTypes.oneOf([
    'bordered',
    'boxed',
  ]),
  showReturnReason: PropTypes.bool,
};
